/**
 * Rule 17 (MDP-641): after a checkpoint is activated, an administrator with no current
 * Architect badge on the responsibility is offered a self-appointment. Callers only
 * `requestPrompt(...)` (fire and forget); activation never waits on, or depends on, the answer.
 * The dialog itself is rendered by `SelfAppointArchitectPrompt` (mounted once in App.vue).
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import store from "@/store";
import { createProficiencyBadge, getEmployeeBadges } from "@/api";
import { resolveStoredAuthEmployeeId } from "@components/utils/authEmployeeContext";
import { buildAppointBadgePayload, isCurrentUserAdmin } from "@components/utils/appointedBadge";

// Architect badges may be reported as the stable key, the legacy API string or the level number.
const ARCHITECT_LEVELS = new Set(["architect", "master", "4"]);

function parseAccountId() {
  const raw = localStorage.getItem("currentAccountID");
  if (raw === null || raw === undefined || raw === "") return null;
  const direct = Number(raw);
  if (Number.isFinite(direct) && direct > 0) return direct;
  try {
    const n = Number(JSON.parse(raw));
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function isFlagOn() {
  return store?.getters?.["featureFlags/ideaMDP641"] === true;
}

function isMasterBadge(badge) {
  const raw = badge?.level ?? badge?.currentBadge?.level;
  return ARCHITECT_LEVELS.has(String(raw ?? "").toLowerCase());
}

export const useSelfAppointPromptStore = defineStore("selfAppointPrompt", () => {
  const queue = ref([]);
  const inFlight = new Set();
  const submitting = ref(false);
  /** Incremented after a successful self-appointment; badge lists watch it to refetch. */
  const refreshGeneration = ref(0);

  const current = computed(() => queue.value[0] || null);

  async function requestPrompt({ responsibilityId, responsibilityName = "" } = {}) {
    const key = String(responsibilityId ?? "");
    if (!key || !isFlagOn() || !isCurrentUserAdmin()) return false;
    if (inFlight.has(key) || queue.value.some((p) => p.key === key)) return false;

    const accountId = parseAccountId();
    const employeeId = resolveStoredAuthEmployeeId();
    if (!accountId || !employeeId) return false;

    inFlight.add(key);
    try {
      const res = await getEmployeeBadges(accountId, employeeId, {
        status: "current",
        responsibility_id: responsibilityId,
      });
      const list = res?.data?.results ?? res?.data ?? [];
      const hasArchitect = Array.isArray(list) && list.some(isMasterBadge);
      if (hasArchitect) return false;
      queue.value.push({ key, responsibilityId, responsibilityName, accountId, employeeId });
      return true;
    } catch (e) {
      console.error("Self-appointment prompt: could not read current badges", e);
      return false;
    } finally {
      inFlight.delete(key);
    }
  }

  function dismissCurrent() {
    queue.value.shift();
  }

  /** Appoints Architect to the administrator's own employee. Resolves true on success. */
  async function confirmCurrent() {
    const prompt = current.value;
    if (!prompt || submitting.value) return false;
    submitting.value = true;
    try {
      await createProficiencyBadge(
        prompt.accountId,
        buildAppointBadgePayload({
          employeeId: prompt.employeeId,
          responsibilityId: prompt.responsibilityId,
          level: "master",
          notify: false,
        })
      );
      queue.value.shift();
      refreshGeneration.value += 1;
      return true;
    } catch (e) {
      queue.value.shift();
      throw e;
    } finally {
      submitting.value = false;
    }
  }

  return {
    queue,
    current,
    submitting,
    refreshGeneration,
    requestPrompt,
    dismissCurrent,
    confirmCurrent,
  };
});
